/**
 * BillingItemsTable (アプリ固有)
 *
 * 請求明細の入力テーブル。1 行 = 項目(科目) / カテゴリ(貸室/付帯/その他) / 税区分 / 金額。
 * - ドラッグで並べ替え (@dnd-kit)
 * - 科目セレクト (新規追加可) / 税区分セレクト (非課税・課税10%) / 金額入力 / 行削除
 * - 下部に金額サマリー (小計 / 消費税 / 請求金額 / 入金済 / 未入金)
 *
 * 賃貸収入の税区分は **非課税 / 課税10%** の 2 区分のみ。金額は税抜。
 * 税区分やカテゴリ(revenue_group)に依存するドメイン固有 UI のため shared-components ではなく
 * アプリ側に置く。controlled (items/onChange)。科目一覧 / 追加ハンドラ / 入金額は親から props で渡す。
 */

import React from "react";
import { Box, Stack, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CustomTextInput,
  CustomCategorySelectSingle,
  CustomButton,
  CustomModal,
  RequireIcon,
  colors,
  type CategoryOption,
} from "@ippon/shared-components";

export type BillingTaxCategory = "taxable" | "non_taxable";

export interface BillingItemRow {
  /** 行 ID (UI キー / 並べ替えの一意性) */
  item_id: string;
  /** 紐づくカテゴリ (= BudgetCategory.category_id) */
  category_id: string;
  /** 科目名 (項目列に表示) */
  name: string;
  /** 税抜金額 (円, 文字列で扱う — 入力中の空文字を許容) */
  amount: string;
  /** 税区分。非課税 / 課税(10%) のみ */
  tax_category: BillingTaxCategory;
  /** ローカル新規追加 (まだ BudgetCategory 未作成)。親の保存時に永続化し category_id を確定。 */
  isNew?: boolean;
}

export interface BillingCategoryOption {
  /** category_id */
  value: string;
  /** 科目名 */
  label: string;
  /** カテゴリ表示 (貸室/付帯/その他)。項目選択時にカテゴリ列へ出す。 */
  categoryLabel?: string;
}

export interface BillingItemsTableProps {
  items: BillingItemRow[];
  onChange: (next: BillingItemRow[]) => void;
  /** 科目選択肢 (BudgetCategory 由来。アプリ層で revenue_group ラベル付きで渡す) */
  categoryOptions: BillingCategoryOption[];
  /**
   * 科目の新規追加を許可。true で「科目を選択」に新規入力 UI を出す。
   * 追加は **ローカルのみ** (isNew 行・category_id 空)。実際の BudgetCategory 作成は
   * 親の保存時にまとめて行う (キャンセル時の orphan カテゴリを防ぐ)。
   */
  allowAddCategory?: boolean;
  /** 科目の新規登録 (即時作成)。明細項目名 + 収益カテゴリ(revenue_group) を渡し、
   *  作成された category_id を返す (親が createBudgetCategory + 科目一覧再取得)。
   *  指定時、科目セレクト先頭に「新規登録」を出しモーダルで登録する。 */
  onAddCategory?: (
    name: string,
    revenueGroup: string,
  ) => Promise<string | undefined>;
  /** 収益カテゴリの選択肢 (新規登録モーダル用)。未指定なら貸室/付帯/その他の3つ。
   *  検索+新規入力で任意の収益カテゴリも追加できる。 */
  revenueGroupOptions?: { value: string; label: string }[];
  /** 入金額 (サマリーの入金済/未入金 表示用)。未指定なら入金済/未入金行を出さない。 */
  paidAmount?: number;
  /** true で読み取り専用 (セレクト/入力を出さず値表示のみ)。 */
  readOnly?: boolean;
  /** 「明細を追加」ボタンのラベル */
  addLabel?: string;
  /** 明細必須エラー文言。指定すると「明細項目」見出し直下に赤字で表示し、
   *  scrollToFirstError 用に data-rhf-field="_items" を付与する。 */
  errorMessage?: string;
  /** 科目(明細項目)未選択の行 item_id。該当行の科目セレクト直下に
   *  「科目は必須です」をインライン表示する (支出側の金額必須と同じ挙動)。 */
  categoryRequiredErrorItemIds?: Set<string>;
}

const TAX_OPTIONS: CategoryOption[] = [
  { value: "non_taxable", label: "非課税" },
  { value: "taxable", label: "課税10%" },
];

// 収益カテゴリの既定 (system 3つ)。親から渡されればカスタム収益カテゴリも含む。
const DEFAULT_REVENUE_GROUPS = [
  { value: "stock_rent", label: "貸室" },
  { value: "stock_other", label: "付帯" },
  { value: "other", label: "その他" },
];

const TAX_RATE: Record<BillingTaxCategory, number> = {
  taxable: 10,
  non_taxable: 0,
};

const genId = () =>
  `bil_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const yen = (n: number): string =>
  `¥${(Number.isFinite(n) ? n : 0).toLocaleString("ja-JP")}`;

/** 行の消費税額 = floor(税抜 × 税率 / 100)。非課税は 0。 */
const rowTax = (row: BillingItemRow): number => {
  const amt = Number(row.amount) || 0;
  const rate = TAX_RATE[row.tax_category] ?? 0; // 旧データ等で未設定なら非課税扱い
  return Math.floor((amt * rate) / 100);
};

/** 金額の桁数上限 (業務上限)。これを超えると BE で DynamoDB SDK の
 *  MAX_SAFE_INTEGER エラーになるため、その前にフォーム側で弾く (12桁まで許可)。 */
export const BILLING_AMOUNT_MAX_DIGITS = 12;
/** 金額が桁数上限 (= 13桁以上) を超えているか。 */
export const isBillingAmountTooLong = (amount: string | number): boolean => {
  const digits = String(amount ?? "").replace(/[^0-9]/g, "");
  return digits.length > BILLING_AMOUNT_MAX_DIGITS;
};
/** 明細のいずれかの金額が桁数上限を超えているか (送信ブロック判定用)。
 *  amount を持つ配列なら型を問わず判定できる (収入/支出 双方の明細で使う)。 */
export const hasBillingAmountError = (
  items: ReadonlyArray<{ amount: string | number }>,
): boolean => items.some((it) => isBillingAmountTooLong(it.amount));

const COL = {
  grip: 28,
  // 明細項目は少し狭め、その分 金額入力を広げる。
  item: "1.6 1 0",
  // 「収益カテゴリ」ラベルが収まるよう少し広げる。
  category: "1.5 1 0",
  tax: "1.3 1 0",
  amount: "1.7 1 0",
  del: 28,
};

// 入力セル共通: CustomCategorySelectSingle が label="" でも空ラベル領域 (.inputLabel) を
// 常時描画して行がガタつくため非表示にし、内部 margin も殺して高さを揃える。
const inputCellSx = {
  minWidth: 0,
  // セレクト(CustomCategorySelectSingle)は label="" でも .inputLabel を常時描画し、
  // padding/margin 分(約5px)セレクトだけ下がる。上揃え時に金額/カテゴリと縦ズレ
  // するため、高さ・余白ごと完全に潰して入力を最上部に揃える。
  "& .inputLabel": {
    display: "none !important",
    height: "0 !important",
    minHeight: "0 !important",
    padding: "0 !important",
    margin: "0 !important",
  },
  "& > *": { margin: "0 !important" },
};

const HeaderCell: React.FC<{
  children: React.ReactNode;
  flex?: string;
  width?: number;
  align?: "left" | "right";
  divider?: boolean;
  /** セル左の余白 (MUI spacing)。支出テーブルのセル padding(8px) に合わせるために使う。 */
  pl?: number;
  /** ラベルだけを左へずらす量 (px)。セル幅は変えないので他列の配置に影響しない。
   *  先頭列(明細項目)をグリップ列ぶんの余白を詰めて左端(支出と同じ左余白)に揃えるために使う。 */
  labelOffsetLeft?: number;
}> = ({ children, flex, width, align = "left", divider, pl, labelOffsetLeft }) => (
  <Box
    sx={{
      flex,
      width,
      minWidth: 0,
      pl,
      textAlign: align,
      // 列間の区切り線 (gap の中央に縦線)。支出テーブル (ExpenseBreakdownTable) と
      // 同じ見た目にする: ヘッダー行(py:1)の上下 6px を空けた長さ。
      // 収入のヘッダーセルはテキスト高しかないため、セル外側へ 2px 延ばして
      // 行の上下 6px 空き (= 支出の top/bottom:6px) と同じ縦長にする。
      ...(divider && {
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          right: -4,
          top: -2,
          bottom: -2,
          width: "1px",
          backgroundColor: colors.border.light,
        },
      }),
    }}
  >
    <Typography
      fontSize={13}
      fontWeight={600}
      color={colors.text.primary}
      sx={labelOffsetLeft ? { ml: `${labelOffsetLeft}px` } : undefined}
    >
      {children}
    </Typography>
  </Box>
);

interface SortableRowProps {
  row: BillingItemRow;
  categoryOptions: BillingCategoryOption[];
  /** 指定時、科目セレクト先頭に「新規登録」を出し、押すとこれを呼ぶ (モーダルを開く)。 */
  onAddNew?: () => void;
  readOnly?: boolean;
  /** 科目未選択の必須エラーを表示するか (送信時に親から指定)。 */
  categoryRequiredError?: boolean;
  onUpdate: (patch: Partial<BillingItemRow>) => void;
  onRemove: () => void;
}

const SortableRow: React.FC<SortableRowProps> = ({
  row,
  categoryOptions,
  onAddNew,
  readOnly,
  categoryRequiredError,
  onUpdate,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.item_id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const categoryLabel =
    categoryOptions.find((o) => o.value === row.category_id)?.categoryLabel ||
    "-";

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        gap: 1,
        // 科目セレクトに必須エラー文が出ても他セルがズレないよう上揃え。
        // 入力(高さ34)に合わせ、グリップ/削除アイコンは下記で中央寄せにする。
        alignItems: "flex-start",
        px: 1,
        py: 0.75,
        borderTop: `1px solid ${colors.border.light}`,
        backgroundColor: colors.background.default,
      }}
    >
      {/* ドラッグハンドル */}
      <Box
        sx={{
          width: COL.grip,
          height: { sm: "34px", xs: "32px" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!readOnly && (
          <IconButton
            size="small"
            aria-label="並べ替え"
            {...attributes}
            {...listeners}
            sx={{ cursor: "grab", color: colors.icon.secondary, p: 0.25 }}
          >
            <DragIndicatorIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      {/* 項目 (科目) */}
      <Box sx={{ flex: COL.item, ...inputCellSx }}>
        {readOnly ? (
          <Typography fontSize={14} color={colors.text.primary} noWrap>
            {row.name || "-"}
          </Typography>
        ) : (
          <CustomCategorySelectSingle
            label=""
            name={`bil_cat_${row.item_id}`}
            searchable={false}
            isRequired={categoryRequiredError}
            errors={
              categoryRequiredError
                ? { [`bil_cat_${row.item_id}`]: { message: "科目は必須です" } }
                : {}
            }
            options={categoryOptions.map((o) => ({
              value: o.value,
              // 左=明細項目 / 右端(グレー)=収益カテゴリ (between)
              label: o.label,
              rightLabel: o.categoryLabel,
            }))}
            selectedValue={row.category_id || ""}
            onValueChange={(v) => {
              const opt = categoryOptions.find((o) => o.value === v);
              onUpdate({ category_id: String(v), name: opt?.label || row.name });
            }}
            onAddNew={onAddNew}
            placeholder="明細科目を追加"
            inputWidth="100%"
            // 科目セルは狭いので、候補ドロップダウンは広げる。
            menuWidth={240}
          />
        )}
      </Box>

      {/* カテゴリ (科目から導出・表示のみ) */}
      <Box
        sx={{
          flex: COL.category,
          minWidth: 0,
          // ヘッダーと同じく左 8px の余白を入れる (支出テーブルのセル padding に合わせる)。
          pl: 1,
          // 行は上揃えなので、コントロール(高さ34)の中央に表示テキストを合わせる。
          height: { sm: "34px", xs: "32px" },
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography fontSize={13} color={colors.text.primary} noWrap>
          {categoryLabel}
        </Typography>
      </Box>

      {/* 税区分 */}
      <Box sx={{ flex: COL.tax, pl: 1, ...inputCellSx }}>
        {readOnly ? (
          <Typography fontSize={13} color={colors.text.primary} noWrap>
            {TAX_OPTIONS.find((t) => t.value === row.tax_category)?.label || "-"}
          </Typography>
        ) : (
          <CustomCategorySelectSingle
            label=""
            name={`bil_tax_${row.item_id}`}
            options={TAX_OPTIONS}
            selectedValue={row.tax_category || "non_taxable"}
            onValueChange={(v) =>
              onUpdate({
                tax_category: (v as BillingTaxCategory) || "non_taxable",
              })
            }
            inputWidth="100%"
            searchable={false}
            clearable={false}
          />
        )}
      </Box>

      {/* 金額 (税抜) */}
      <Box sx={{ flex: COL.amount, pl: 1, ...inputCellSx }}>
        {readOnly ? (
          <Typography
            fontSize={14}
            color={colors.text.primary}
            textAlign="right"
            noWrap
          >
            {yen(Number(row.amount) || 0)}
          </Typography>
        ) : (
          <CustomTextInput
            label=""
            name={`bil_amt_${row.item_id}`}
            type="number"
            register={
              ((..._args: unknown[]) => ({
                onChange: () => {},
                onBlur: () => {},
                ref: () => {},
                name: "",
              })) as any
            }
            errors={{}}
            showYen
            watch={
              ((fieldName: string) =>
                fieldName === `bil_amt_${row.item_id}` ? row.amount : "") as any
            }
            setValue={
              ((fieldName: string, v: any) => {
                if (fieldName === `bil_amt_${row.item_id}`) {
                  onUpdate({ amount: String(v ?? "") });
                }
              }) as any
            }
            inputWidth="100%"
          />
        )}
      </Box>

      {/* 削除 */}
      <Box
        sx={{
          width: COL.del,
          height: { sm: "34px", xs: "32px" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!readOnly && (
          <IconButton
            size="small"
            aria-label="行を削除"
            onClick={onRemove}
            sx={{ color: colors.icon.primary, p: 0.25 }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

const SummaryRow: React.FC<{
  label: string;
  value: string;
  bold?: boolean;
  color?: string;
}> = ({ label, value, bold, color }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 0.25,
    }}
  >
    <Typography
      fontSize={bold ? 14 : 13}
      fontWeight={bold ? 700 : 400}
      color={colors.text.secondary}
    >
      {label}
    </Typography>
    <Typography
      fontSize={bold ? 18 : 14}
      fontWeight={bold ? 700 : 400}
      color={color || colors.text.primary}
    >
      {value}
    </Typography>
  </Box>
);

const BillingItemsTable: React.FC<BillingItemsTableProps> = ({
  items,
  onChange,
  categoryOptions,
  onAddCategory,
  revenueGroupOptions = DEFAULT_REVENUE_GROUPS,
  paidAmount,
  readOnly,
  addLabel = "明細を追加",
  errorMessage,
  categoryRequiredErrorItemIds,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  // 科目「新規登録」モーダルの状態 (収益カテゴリ + 明細項目)
  const [addModalRowId, setAddModalRowId] = React.useState<string | null>(null);
  const [addCat, setAddCat] = React.useState("");
  const [addName, setAddName] = React.useState("");
  const [addSaving, setAddSaving] = React.useState(false);

  const updateRow = (id: string, patch: Partial<BillingItemRow>) =>
    onChange(items.map((it) => (it.item_id === id ? { ...it, ...patch } : it)));

  // 即時作成: 収益カテゴリ + 明細項目 → 親が createBudgetCategory → category_id を行に反映。
  const submitAddCategory = async () => {
    if (!onAddCategory || !addModalRowId || !addCat || !addName.trim()) return;
    setAddSaving(true);
    try {
      const id = await onAddCategory(addName.trim(), addCat);
      if (id) {
        updateRow(addModalRowId, {
          category_id: id,
          name: addName.trim(),
          isNew: false,
        });
      }
      setAddModalRowId(null);
      setAddCat("");
      setAddName("");
    } finally {
      setAddSaving(false);
    }
  };

  const removeRow = (id: string) =>
    onChange(items.filter((it) => it.item_id !== id));

  const addRow = () =>
    onChange([
      ...items,
      {
        item_id: genId(),
        category_id: "",
        name: "",
        amount: "",
        tax_category: "non_taxable",
      },
    ]);

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.item_id === active.id);
    const newIndex = items.findIndex((i) => i.item_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(items, oldIndex, newIndex));
  };

  const subtotal = items.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const taxTotal = items.reduce((s, r) => s + rowTax(r), 0);
  const total = subtotal + taxTotal;

  return (
    // data-rhf-field は scrollToFirstError のスクロール先 (明細必須エラー時)。
    // setState 直後に scroll が走るため、エラー有無に関わらず常時付与しておく。
    <Stack spacing={1.5} data-rhf-field="_items">
      <Box
        sx={{
          border: `1px solid ${colors.border.light}`,
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {/* ヘッダー */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            px: 1,
            // 支出テーブル (ExpenseBreakdownTable headerCellSx) の py:1 と高さを揃える。
            py: 1,
            backgroundColor: colors.background.light,
          }}
        >
          <Box sx={{ width: COL.grip }} />
          {/* 先頭列はグリップ列ぶん右に寄るので、ラベルだけ左へ引いて
              支出テーブルと同じ左余白の位置に左寄せする (実測で -34px が最適)。 */}
          <HeaderCell flex={COL.item} divider labelOffsetLeft={-34}>
            明細項目
            {!readOnly && (
              <Box
                component="span"
                sx={{ display: "inline-flex", verticalAlign: "middle", ml: 0.5 }}
              >
                <RequireIcon />
              </Box>
            )}
          </HeaderCell>
          <HeaderCell flex={COL.category} divider pl={1}>
            収益カテゴリ
          </HeaderCell>
          <HeaderCell flex={COL.tax} divider pl={1}>
            税区分
          </HeaderCell>
          <HeaderCell flex={COL.amount} pl={1}>
            金額
          </HeaderCell>
          <Box sx={{ width: COL.del }} />
        </Box>

        {/* 明細必須エラー (「明細項目」見出しの直下に表示) */}
        {errorMessage && (
          <Box sx={{ color: "#DA384C", fontSize: 12, px: 1, py: 0.75 }}>
            {errorMessage}
          </Box>
        )}

        {/* 金額の桁数オーバー (12桁以上) を赤字で表示。送信は親側でブロックする。 */}
        {!readOnly && hasBillingAmountError(items) && (
          <Box sx={{ color: "#DA384C", fontSize: 12, px: 1, py: 0.75 }}>
            {BILLING_AMOUNT_MAX_DIGITS}桁以内で入力してください
          </Box>
        )}

        {/* 明細行 (ドラッグ並べ替え) */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((i) => i.item_id)}
            strategy={verticalListSortingStrategy}
          >
            {items.map((row) => (
              <SortableRow
                key={row.item_id}
                row={row}
                categoryOptions={categoryOptions}
                onAddNew={
                  onAddCategory
                    ? () => {
                        setAddCat("");
                        setAddName("");
                        setAddModalRowId(row.item_id);
                      }
                    : undefined
                }
                readOnly={readOnly}
                categoryRequiredError={
                  categoryRequiredErrorItemIds?.has(row.item_id) ?? false
                }
                onUpdate={(patch) => updateRow(row.item_id, patch)}
                onRemove={() => removeRow(row.item_id)}
              />
            ))}
          </SortableContext>
        </DndContext>

        {/* 明細追加 — 支出の内訳テーブル (ExpenseBreakdownTable) と同じく
            枠の内側・末尾に dashed ボタンを置く。 */}
        {!readOnly && (
          <Box sx={{ p: 1, borderTop: `1px solid ${colors.border.light}` }}>
            <CustomButton
              label={`＋${addLabel}`}
              buttonCategory="clearPrimary"
              onClick={addRow}
              sx={{ width: "100%", borderStyle: "dashed" }}
            />
          </Box>
        )}
      </Box>

      {/* 金額サマリー */}
      <Box
        sx={{
          backgroundColor: colors.background.lighter,
          borderRadius: "8px",
          p: 2,
        }}
      >
        <SummaryRow label="小計" value={yen(subtotal)} />
        <SummaryRow label="消費税" value={yen(taxTotal)} />
        <Box sx={{ borderTop: `1px solid ${colors.border.light}`, my: 1 }} />
        <SummaryRow label="請求金額" value={yen(total)} bold />
        {paidAmount != null && (
          <>
            <SummaryRow
              label="入金済"
              value={yen(paidAmount)}
              color={colors.status.success}
            />
            {paidAmount < total && (
              <SummaryRow
                label="未入金"
                value={yen(total - paidAmount)}
                color={colors.status.warning}
              />
            )}
          </>
        )}
      </Box>

      {/* 科目「新規登録」モーダル: 収益カテゴリ + 明細項目 をセット登録 (即時作成) */}
      <CustomModal
        openModal={addModalRowId !== null}
        handleCloseModal={() => setAddModalRowId(null)}
        title="科目を新規登録"
        modalType="deleteModal"
        hideCloseButton
      >
        <Stack spacing={0} sx={{ mt: 1 }}>
          <Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                mb: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              収益カテゴリ
              <Box component="span" sx={{ display: "flex", ml: 0.5 }}>
                <RequireIcon />
              </Box>
            </Typography>
            <CustomCategorySelectSingle
              label=""
              name="add_cat_revenue_group"
              searchable
              clearable={false}
              options={revenueGroupOptions}
              selectedValue={addCat}
              onValueChange={setAddCat}
              // 既存に無い収益カテゴリは検索欄から新規入力 → そのまま値に使う。
              onAddNewOption={(name) => setAddCat(name.trim())}
              placeholder="選択してください"
              inputWidth="100%"
            />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                mb: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              明細項目
              <Box component="span" sx={{ display: "flex", ml: 0.5 }}>
                <RequireIcon />
              </Box>
            </Typography>
            <Box
              component="input"
              value={addName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setAddName(e.target.value)
              }
              placeholder="例: 賃料"
              sx={{
                width: "100%",
                height: 38,
                px: 1.25,
                fontSize: 13,
                border: "1px solid #D9D9D9",
                borderRadius: "8px",
                outline: "none",
                boxSizing: "border-box",
                "&:focus": { borderColor: "#0B9DBD" },
              }}
            />
          </Box>
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "center", mt: 2 }}
          >
            <CustomButton
              label="戻る"
              buttonCategory="cancelModal"
              onClick={() => setAddModalRowId(null)}
            />
            <CustomButton
              label="登録"
              buttonCategory="submit"
              onClick={() => void submitAddCategory()}
              disabled={!addCat || !addName.trim() || addSaving}
              sx={{ width: "50% !important" }}
            />
          </Box>
        </Stack>
      </CustomModal>
    </Stack>
  );
};

export default BillingItemsTable;
